// ============================================================================
// Client telemetry service — batched daily-aggregate usage counters.
//
// Design: events (sessions, page views, feature usage) accumulate in an
// in-memory buffer mirrored to localStorage, and are flushed as ONE Firestore
// write (setDoc merge + increment) to the caller's own daily doc:
//   niv_telemetry_daily_therapist/{clinicId}_{therapistId}_{dateKey}
// Clinic-level roll-ups and scores are computed by the nightly
// nightlyTelemetryScoring Cloud Function — the client never writes them.
//
// Telemetry deliberately does NOT go through firebase.js write helpers, so it
// bypasses guardTrialWrite — expired-trial clinics must still report usage and
// must never see a TrialExpiredError from analytics. Every public function is
// wrapped so telemetry can never break the app; ~95% accuracy is acceptable.
// ============================================================================

import { getApps } from "firebase/app";
import {
  getFirestore,
  doc,
  setDoc,
  increment,
  serverTimestamp,
} from "firebase/firestore";
import { getIsraelDateString } from "../utils/dateUtils";
import { getDeviceType, isStandalonePWA } from "../utils/deviceUtils";

const THERAPIST_DAILY_COLLECTION = "niv_telemetry_daily_therapist";

const SESSION_STORAGE_KEY = "ck_tm_session";
const BUFFER_STORAGE_PREFIX = "ck_tm_buf_"; // + therapistId
const SESSION_IDLE_MINUTES = 30;
const FLUSH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const FLUSH_EVENT_THRESHOLD = 50; // events per buffer before forced flush
const PAGE_VIEW_DEDUPE_MS = 5000;
const MAX_CONSECUTIVE_FLUSH_FAILURES = 3;

// Canonical English page keys (Hebrew labels live in the NiV dashboard UI)
export const PAGE_KEYS = {
  HOME: "home",
  PATIENTS: "patients",
  PATIENT_PROFILE: "patientProfile",
  TREATMENT_DOC: "treatmentDoc",
  CALENDAR: "calendar",
  TASKS: "tasks",
  FINANCE: "finance",
  FINANCE_REPORTS: "financeReports",
  REPORTS: "reports",
  SETTINGS: "settings",
  SUPPORT: "support",
  LIVE_SESSION: "liveSession",
  ADMIN_SNAPSHOT: "adminSnapshot",
  ADMIN_OVERVIEW: "adminOverview",
  ADMIN_PATIENTS: "adminPatients",
  ADMIN_CALENDAR: "adminCalendar",
  ADMIN_TASKS: "adminTasks",
  ADMIN_ACTIVITY_LOGS: "adminActivityLogs",
  ADMIN_SETTINGS: "adminSettings",
};

export const FEATURE_KEYS = {
  ROOMS: "rooms",
  GOOGLE_CALENDAR_SYNC: "googleCalendarSync",
  FINANCE_ACTIONS: "financeActions",
  MORNING_INVOICE: "morningInvoice",
  DIGITAL_INVOICE: "digitalInvoice",
  MONTHLY_REPORTS: "monthlyReports",
  QUESTIONNAIRES: "questionnaires",
  DIGITAL_SIGNATURE: "digitalSignature",
  LIVE_SESSION: "liveSession",
  GAMES: "games",
  HELP: "help",
  PENDING_ACTIONS_DEMO: "pendingActionsDemo",
  AI_TREATMENT_CHAT: "aiTreatmentChat",
};

// Panel/tab "was it ever opened" keys. Deliberately a SEPARATE map from
// `features`: the features map feeds the nightly value score, so folding view
// counters into it would silently move every clinic's score. Views only feed
// the feature-discovery nudge engine.
export const VIEW_KEYS = {
  CALENDAR_SETTINGS_GOOGLE: "calendarSettingsGoogle",
  CALENDAR_SETTINGS_SMS: "calendarSettingsSms",
  CALENDAR_SETTINGS_BOOKING: "calendarSettingsBooking",
};

const EXACT_PATH_TO_PAGE_KEY = {
  "/dashboard": PAGE_KEYS.HOME,
  "/dashboard/patients": PAGE_KEYS.PATIENTS,
  "/dashboard/calendar": PAGE_KEYS.CALENDAR,
  "/dashboard/tasks": PAGE_KEYS.TASKS,
  "/dashboard/finance": PAGE_KEYS.FINANCE,
  "/dashboard/finance/reports": PAGE_KEYS.FINANCE_REPORTS,
  "/dashboard/reports": PAGE_KEYS.REPORTS,
  "/dashboard/settings": PAGE_KEYS.SETTINGS,
  "/dashboard/support": PAGE_KEYS.SUPPORT,
  "/dashboard/admin/snapshot": PAGE_KEYS.ADMIN_SNAPSHOT,
  "/dashboard/admin/overview": PAGE_KEYS.ADMIN_OVERVIEW,
  "/dashboard/admin/patients": PAGE_KEYS.ADMIN_PATIENTS,
  "/dashboard/admin/calendar": PAGE_KEYS.ADMIN_CALENDAR,
  "/dashboard/admin/tasks": PAGE_KEYS.ADMIN_TASKS,
  "/dashboard/admin/activity-logs": PAGE_KEYS.ADMIN_ACTIVITY_LOGS,
  "/dashboard/admin/settings": PAGE_KEYS.ADMIN_SETTINGS,
};

export const pathToPageKey = (pathname) => {
  if (!pathname) return null;
  if (pathname.startsWith("/dashboard/niv")) return null; // never track NiV admin
  if (EXACT_PATH_TO_PAGE_KEY[pathname]) return EXACT_PATH_TO_PAGE_KEY[pathname];
  if (/^\/dashboard\/patients\/[^/]+\/treatments\//.test(pathname)) {
    return PAGE_KEYS.TREATMENT_DOC;
  }
  if (pathname.startsWith("/dashboard/patients/")) return PAGE_KEYS.PATIENT_PROFILE;
  if (pathname.startsWith("/dashboard/live-session")) return PAGE_KEYS.LIVE_SESSION;
  if (pathname.startsWith("/dashboard/reports/")) return PAGE_KEYS.REPORTS;
  return null;
};

// ----------------------------------------------------------------------------
// Internal state
// ----------------------------------------------------------------------------

let identity = null; // { clinicId, clinicName, therapistId, therapistName }
let buffer = null; // { dateKey, events, sessions, sessionsByDevice, pwaStandaloneSessions, pageViews, features }
let flushTimer = null;
let flushInFlight = false;
let consecutiveFlushFailures = 0;
let circuitOpen = false;
let lastPageView = { key: null, at: 0 };
let listenersAttached = false;

const emptyBuffer = (dateKey) => ({
  dateKey,
  events: 0,
  sessions: 0,
  sessionsByDevice: {},
  pwaStandaloneSessions: 0,
  pageViews: {},
  features: {},
  views: {}, // tab/panel view counters (nudge engine only, not scored)
  pwaInstallPrompt: {}, // action -> count (shown/install/steps/copyLink/dismiss/never/installed/declined)
});

const logTelemetryError = (code, message, error) => {
  // Deliberately console.warn, not console.error — the global console.error
  // interceptor would otherwise turn telemetry hiccups into niv_error_logs.
  try {
    console.warn(`[${code}] ${message}`, {
      errorCode: code,
      errorMessage: error?.message || String(error),
      timestamp: new Date().toISOString(),
    });
  } catch (_) {
    /* never throw from telemetry */
  }
};

const bufferStorageKey = () =>
  identity ? `${BUFFER_STORAGE_PREFIX}${identity.therapistId}` : null;

const persistBuffer = () => {
  try {
    const key = bufferStorageKey();
    if (!key || !buffer) return;
    localStorage.setItem(key, JSON.stringify(buffer));
  } catch (e) {
    /* storage full/blocked — in-memory buffer still works */
  }
};

const clearPersistedBuffer = () => {
  try {
    const key = bufferStorageKey();
    if (key) localStorage.removeItem(key);
  } catch (e) {
    /* ignore */
  }
};

const readPersistedBuffer = () => {
  try {
    const key = bufferStorageKey();
    if (!key) return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.dateKey) return null;
    return parsed;
  } catch (e) {
    return null;
  }
};

const getDb = () => getFirestore(getApps()[0]);

// ----------------------------------------------------------------------------
// Session detection
// ----------------------------------------------------------------------------

const touchSession = () => {
  // Returns true when this touch STARTS a new session.
  try {
    const now = Date.now();
    let isNew = false;
    let session = null;
    try {
      session = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY) || "null");
    } catch (_) {
      session = null;
    }
    if (
      !session ||
      !session.lastActiveAt ||
      now - session.lastActiveAt > SESSION_IDLE_MINUTES * 60 * 1000
    ) {
      isNew = true;
      session = { startedAt: now, lastActiveAt: now };
    } else {
      session.lastActiveAt = now;
    }
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    return isNew;
  } catch (e) {
    return false;
  }
};

const recordSessionStart = () => {
  buffer.sessions += 1;
  buffer.events += 1;
  const device = getDeviceType();
  buffer.sessionsByDevice[device] = (buffer.sessionsByDevice[device] || 0) + 1;
  if (isStandalonePWA()) buffer.pwaStandaloneSessions += 1;
};

// ----------------------------------------------------------------------------
// Buffer + flush
// ----------------------------------------------------------------------------

const ensureBufferForToday = () => {
  const dateKey = getIsraelDateString(new Date());
  if (!buffer || buffer.dateKey !== dateKey) {
    // Day rolled over (or first event) — flush what we have, start fresh.
    if (buffer && buffer.events > 0) flush("dayRollover");
    buffer = emptyBuffer(dateKey);
  }
  return buffer;
};

const buildFlushPayload = (buf) => {
  const payload = {
    clinicId: identity.clinicId,
    clinicName: identity.clinicName || "",
    therapistId: identity.therapistId,
    therapistName: identity.therapistName || "",
    dateKey: buf.dateKey,
    lastSeenAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (buf.sessions > 0) payload.sessions = increment(buf.sessions);
  if (buf.pwaStandaloneSessions > 0) {
    payload.pwaStandaloneSessions = increment(buf.pwaStandaloneSessions);
  }
  for (const [device, count] of Object.entries(buf.sessionsByDevice)) {
    payload[`sessionsByDevice.${device}`] = increment(count);
  }
  for (const [page, count] of Object.entries(buf.pageViews)) {
    payload[`pageViews.${page}`] = increment(count);
  }
  for (const [feature, count] of Object.entries(buf.features)) {
    payload[`features.${feature}`] = increment(count);
  }
  for (const [view, count] of Object.entries(buf.views || {})) {
    payload[`views.${view}`] = increment(count);
  }
  for (const [action, count] of Object.entries(buf.pwaInstallPrompt || {})) {
    payload[`pwaInstallPrompt.${action}`] = increment(count);
  }
  return payload;
};

const flushBufferObject = async (buf) => {
  const docId = `${identity.clinicId}_${identity.therapistId}_${buf.dateKey}`;
  const ref = doc(getDb(), THERAPIST_DAILY_COLLECTION, docId);
  const payload = buildFlushPayload(buf);
  // Dotted-path increments require update semantics; setDoc+merge treats dotted
  // keys as literal field names, so build nested merge objects instead.
  const nested = {};
  for (const [key, value] of Object.entries(payload)) {
    if (key.includes(".")) {
      const [outer, inner] = key.split(".");
      nested[outer] = nested[outer] || {};
      nested[outer][inner] = value;
    } else {
      nested[key] = value;
    }
  }
  await setDoc(ref, nested, { merge: true });
};

export const flush = (reason = "interval") => {
  try {
    if (circuitOpen || !identity || !buffer || buffer.events === 0) return;
    if (flushInFlight) return;
    const toSend = buffer;
    buffer = emptyBuffer(toSend.dateKey);
    flushInFlight = true;
    flushBufferObject(toSend)
      .then(() => {
        consecutiveFlushFailures = 0;
        clearPersistedBuffer();
        persistBuffer(); // persist the (possibly already re-dirtied) new buffer
      })
      .catch((error) => {
        // Merge unsent counts back so nothing is lost; retry on next trigger.
        consecutiveFlushFailures += 1;
        try {
          if (buffer.dateKey === toSend.dateKey) {
            buffer.events += toSend.events;
            buffer.sessions += toSend.sessions;
            buffer.pwaStandaloneSessions += toSend.pwaStandaloneSessions;
            for (const [k, v] of Object.entries(toSend.sessionsByDevice)) {
              buffer.sessionsByDevice[k] = (buffer.sessionsByDevice[k] || 0) + v;
            }
            for (const [k, v] of Object.entries(toSend.pageViews)) {
              buffer.pageViews[k] = (buffer.pageViews[k] || 0) + v;
            }
            for (const [k, v] of Object.entries(toSend.features)) {
              buffer.features[k] = (buffer.features[k] || 0) + v;
            }
            buffer.views = buffer.views || {};
            for (const [k, v] of Object.entries(toSend.views || {})) {
              buffer.views[k] = (buffer.views[k] || 0) + v;
            }
            buffer.pwaInstallPrompt = buffer.pwaInstallPrompt || {};
            for (const [k, v] of Object.entries(toSend.pwaInstallPrompt || {})) {
              buffer.pwaInstallPrompt[k] = (buffer.pwaInstallPrompt[k] || 0) + v;
            }
          }
          persistBuffer();
        } catch (_) {
          /* ignore */
        }
        if (consecutiveFlushFailures >= MAX_CONSECUTIVE_FLUSH_FAILURES) {
          circuitOpen = true;
          logTelemetryError(
            "CK-8462",
            "Telemetry flush circuit opened after repeated failures",
            error
          );
        } else {
          logTelemetryError("CK-8461", `Telemetry flush failed (${reason})`, error);
        }
      })
      .finally(() => {
        flushInFlight = false;
      });
  } catch (error) {
    logTelemetryError("CK-8460", "Telemetry flush error", error);
  }
};

const resurrectLeftoverBuffer = () => {
  // A buffer left in localStorage by a killed tab (possibly a previous day) is
  // flushed directly — counters are keyed by its own dateKey.
  const leftover = readPersistedBuffer();
  if (!leftover || !leftover.events) return;
  clearPersistedBuffer();
  flushBufferObject(leftover).catch((error) => {
    logTelemetryError("CK-8463", "Leftover telemetry buffer flush failed", error);
  });
};

const attachLifecycleListeners = () => {
  if (listenersAttached) return;
  listenersAttached = true;
  const onHide = () => {
    try {
      if (document.visibilityState === "hidden") flush("visibilityHidden");
    } catch (_) {
      /* ignore */
    }
  };
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", () => flush("pagehide"));
};

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------

export const initTelemetry = ({ clinicId, clinicName, therapistId, therapistName }) => {
  try {
    if (!clinicId || !therapistId) return;
    if (identity?.therapistId === therapistId && identity?.clinicId === clinicId) {
      return; // already initialized for this user
    }
    identity = { clinicId, clinicName, therapistId, therapistName };
    circuitOpen = false;
    consecutiveFlushFailures = 0;
    buffer = emptyBuffer(getIsraelDateString(new Date()));

    resurrectLeftoverBuffer();

    if (touchSession()) {
      recordSessionStart();
      persistBuffer();
    }

    if (flushTimer) clearInterval(flushTimer);
    flushTimer = setInterval(() => flush("interval"), FLUSH_INTERVAL_MS);
    attachLifecycleListeners();
  } catch (error) {
    logTelemetryError("CK-8464", "Telemetry init failed", error);
  }
};

export const resetTelemetry = () => {
  try {
    flush("reset");
    if (flushTimer) clearInterval(flushTimer);
    flushTimer = null;
    identity = null;
    buffer = null;
    lastPageView = { key: null, at: 0 };
  } catch (error) {
    logTelemetryError("CK-8465", "Telemetry reset failed", error);
  }
};

export const trackPageView = (pathname) => {
  try {
    if (!identity || circuitOpen) return;
    const pageKey = pathToPageKey(pathname);
    if (!pageKey) return;
    const now = Date.now();
    if (lastPageView.key === pageKey && now - lastPageView.at < PAGE_VIEW_DEDUPE_MS) {
      return;
    }
    lastPageView = { key: pageKey, at: now };

    const buf = ensureBufferForToday();
    if (touchSession()) recordSessionStart();
    buf.pageViews[pageKey] = (buf.pageViews[pageKey] || 0) + 1;
    buf.events += 1;
    persistBuffer();
    if (buf.events >= FLUSH_EVENT_THRESHOLD) flush("threshold");
  } catch (error) {
    logTelemetryError("CK-8466", "trackPageView failed", error);
  }
};

/**
 * Track an interaction with the "install CliniKit" PWA banner.
 * Kept in its own map (not `features`) so it never feeds feature-adoption scoring.
 * @param {"shown"|"install"|"steps"|"copyLink"|"dismiss"|"installed"|"declined"} action
 */
export const trackPwaInstallPrompt = (action) => {
  try {
    if (!identity || circuitOpen || !action) return;
    const buf = ensureBufferForToday();
    buf.pwaInstallPrompt = buf.pwaInstallPrompt || {};
    buf.pwaInstallPrompt[action] = (buf.pwaInstallPrompt[action] || 0) + 1;
    buf.events += 1;
    persistBuffer();
    if (buf.events >= FLUSH_EVENT_THRESHOLD) flush("threshold");
  } catch (error) {
    logTelemetryError("CK-8468", "trackPwaInstallPrompt failed", error);
  }
};

export const trackFeature = (featureKey) => {
  try {
    if (!identity || circuitOpen || !featureKey) return;
    const buf = ensureBufferForToday();
    if (touchSession()) recordSessionStart();
    buf.features[featureKey] = (buf.features[featureKey] || 0) + 1;
    buf.events += 1;
    persistBuffer();
    if (buf.events >= FLUSH_EVENT_THRESHOLD) flush("threshold");
  } catch (error) {
    logTelemetryError("CK-8467", "trackFeature failed", error);
  }
};

// Records that a tab/panel was opened at least once. Callers must dedupe per
// mount/session so re-renders do not inflate `buffer.events` (which drives
// threshold flushes).
export const trackView = (viewKey) => {
  try {
    if (!identity || circuitOpen || !viewKey) return;
    const buf = ensureBufferForToday();
    if (touchSession()) recordSessionStart();
    buf.views = buf.views || {};
    buf.views[viewKey] = (buf.views[viewKey] || 0) + 1;
    buf.events += 1;
    persistBuffer();
    if (buf.events >= FLUSH_EVENT_THRESHOLD) flush("threshold");
  } catch (error) {
    logTelemetryError("CK-8469", "trackView failed", error);
  }
};
